import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, defer } from 'rxjs';
import { catchError, switchMap, take, filter, timeout } from 'rxjs/operators';
import { CaptchaService } from '../captcha.service';
import { MensagemService } from '../mensagem.service';

/**
 * Interceptor que trata a verificação de captcha para endpoints protegidos
 */
export const captchaInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const captchaService = inject(CaptchaService);
    const mensagemService = inject(MensagemService);
    // verifica se a requisição precisa de captcha
    if (!captchaService.requiresCaptcha(req.url)) {
        // A requisição não requer captcha, prosseguir normalmente
        return next(req);
    }
    // Se o captcha não é exigido (configuração desativada), prosseguir normalmente
    if (captchaService.exigeCaptcha === false) {
        return next(req);
    }

    // Verifica se o captcha já foi verificado
    if (captchaService.isVerified && captchaService.token) {
        // Adiciona o token do captcha aos headers da requisição
        const captchaReq = req.clone({
            headers: req.headers.set('X-Captcha-Token', captchaService.token)
        });

        return next(captchaReq).pipe(
            catchError(error => {
                // se o backend retornar erro de captcha inválido, limpar estado do captcha
                if (error.status === 400 && error.error?.captchaInvalid) {
                    captchaService.clear();
                    mensagemService.mostrar('Erro', 'Captcha Inválido', 'Verificação de segurança falhou no servidor. Tente novamente.');
                }
                return throwError(() => error);
            })
        );
    }

    // Se o captcha não foi verificado, notificar o usuário e iniciar o processo de verificação
    mensagemService.mostrar('Captcha', 'Info', 'Verificação de segurança necessária. Complete o captcha para continuar.');

    // acionamento imediato do processamento do captcha
    captchaService.setProcessing(true);

    // retorna um observable adiado (deferred) que espera pela verificação do captcha
    return defer(() => {
        // espera até o captcha ser verificado, então tenta novamente a requisição
        return captchaService.captchaState$.pipe(
            filter(state => state.isVerified && !!state.token), // espera até ser verificado
            take(1), // pega somente o primeiro estado verificado
            timeout(60000), // timeout após 60 segundos
            switchMap(() => {
                // Adiciona o token do captcha aos headers da requisição
                const captchaReq = req.clone({
                    headers: req.headers.set('X-Captcha-Token', captchaService.token!)
                });
                return next(captchaReq);
            }),
            catchError(error => {
                if (error.name === 'TimeoutError') {
                    mensagemService.mostrar('Erro', 'Timeout', 'Verificação de captcha expirou. Tente novamente.');
                }
                return throwError(() => error);
            })
        );
    });
};
